import { notEmpty, theme, useResponsive } from "@product/scmp-sdk";
import { useAsync, useMountEffect, useSyncedRef } from "@react-hookz/web";
import first from "lodash/first";
import type { FunctionComponent } from "react";
import { useEffect, useMemo } from "react";
import { fetchQuery, graphql, useRelayEnvironment } from "react-relay";
import YouTube from "react-youtube";
import type { YouTubePlayer, Options as YoutubePlayerOptions } from "youtube-player/dist/types";

import type { VideoPlayMode } from "~/components/scmp-youtube-video/enums";
import { data as appData, config } from "~/data";
import { useIntersection } from "~/lib/hooks";
import type { scmpYoutubeVideoPlayerQuery } from "~/queries/__generated__/scmpYoutubeVideoPlayerQuery.graphql";

import { AutoplayMode } from "./enums";
import {
  useImaIntegration,
  useManualPlayEffect,
  useOnlyOnePlayerEffect,
  useYouTubeControl,
} from "./hooks";
import { AdContainer, Container, StyledYouTube, VideoContainer } from "./styles";

export type Props = {
  adInfo?: ADInfo;
  allow?: string;
  allowfullscreen?: string;
  enableAutoPlay: boolean;
  entityId?: string;
  frameborder?: string;
  gaActionPrefix?: `Youtube ${
    | "article body"
    | "article header"
    | "article footer"
    | "footer widget"}`;
  height?: string;
  index: number;
  title?: string;
  videoId: string;
  videoPlayMode: VideoPlayMode;
  width?: string;
};

export const SCMPYouTubeVideoPlayer: FunctionComponent<Props> = ({
  adInfo,
  enableAutoPlay,
  entityId,
  gaActionPrefix = "Youtube article body",
  height,
  index,
  title,
  videoId,
  videoPlayMode,
  width,
}) => {
  const environment = useRelayEnvironment();
  const [{ result: data }, { execute }] = useAsync(() =>
    fetchQuery<scmpYoutubeVideoPlayerQuery>(
      environment,
      graphql`
        query scmpYoutubeVideoPlayerQuery($applicationId: String!, $entityId: String!) {
          content(
            contentType: VIDEO
            filter: { entityId: $entityId, applicationId: $applicationId }
          ) {
            headline
            sections {
              value {
                name
              }
            }
          }
        }
      `,
      {
        applicationId: appData.application.scmp.entityUuid,
        entityId: entityId ?? "",
      },
      {
        fetchPolicy: "store-or-network",
      },
    ).toPromise(),
  );

  useMountEffect(() => {
    if (!notEmpty(entityId)) return;
    void execute();
  });

  const zone = adInfo?.zone ?? `video/article/instream1`;
  const adIu = `${config.advertisement.googlePublisherTag.networkId}/${config.advertisement.googlePublisherTag.adUnitPrefix}/${zone}`;
  const playerOptions: YoutubePlayerOptions = {
    embedConfig: {
      adsConfig: {
        adTagParameters: {
          iu: adIu,
        },
        disableAds: true,
        nonPersonalizedAd: true,
      },
      disabledRelatedVideos: true,
      enableIma: true,
      relatedChannels: [appData.youtube.scmpChannelId],
    },
    height,
    playerVars: {
      // https://developers.google.com/youtube/player_parameters
      autoplay: enableAutoPlay ? 1 : 0,
      origin: window.location.origin,
      playsinline: 1,
      rel: 0,
    },
    width,
  };

  // Can play same video in different places
  const uniqueVideoId = ["yt", videoId, entityId, index].filter(notEmpty).join("-");
  const adElementId = `ad-${uniqueVideoId}`;
  const { captureIntersectionElement, intersection } = useIntersection({
    threshold: 0.45,
  });

  const trackingData = useMemo(
    () => ({
      gaActionPrefix,
      sectionString:
        first(data?.content?.sections)
          ?.value?.map(section => section?.name)
          .join("|") ?? "",
      youTubeVideoId: videoId,
      youTubeVideoTitle: title ?? "",
    }),
    [data?.content?.sections, gaActionPrefix, title, videoId],
  );

  const {
    handleOnEnd,
    handleOnPause,
    handleOnReady,
    handleOnStateChange,
    manualPaused,
    player,
    setAutoplayMode,
    setManualPaused,
  } = useYouTubeControl(uniqueVideoId, trackingData);
  useManualPlayEffect(videoPlayMode, player);

  const adData = useMemo(
    () => ({
      adInfo,
      iu: adIu,
    }),
    [adInfo, adIu],
  );
  const { adsManager, initImaManager, isAdPlaying, isAdStarted, isImaInitialized } =
    useImaIntegration(player, intersection?.isIntersecting, setManualPaused, trackingData, adData);

  const latestIsAdPlayingReference = useSyncedRef(isAdPlaying);
  const latestManualPausedReference = useSyncedRef(manualPaused);
  const latestIsAdStarted = useSyncedRef(isAdStarted);

  const isDesktopUp = useResponsive(theme.breakpoints.up("desktop"));
  useEffect(() => {
    if (!player || !intersection || !isImaInitialized || !isDesktopUp) return;
    /**
     *  Autoplay only when:
     *  1. Auto mode on (By default on)
     *  2. No other videos playing
     *  3. The 1st video in the same article
     *  4. User did not pause the video manually
     *  5. Screen size is large than Desktop
     */
    const run = async () => {
      const iframe = await player?.getIframe();
      if (iframe && player && enableAutoPlay && !latestManualPausedReference.current) {
        const handleOnIntersectingWhenWithAdvertisement = () => {
          if (intersection.isIntersecting && latestIsAdStarted.current) {
            adsManager?.resume();
          } else if (latestIsAdPlayingReference.current) {
            adsManager?.pause();
          }
        };

        const handleOnIntersectingInGeneral = () => {
          if (intersection.isIntersecting) {
            if (!latestIsAdStarted.current) {
              void player.playVideo();
            }
            setAutoplayMode(AutoplayMode.Playing);
          } else {
            void player.pauseVideo();
            setAutoplayMode(AutoplayMode.Pausing);
          }
        };

        handleOnIntersectingInGeneral();
        handleOnIntersectingWhenWithAdvertisement();
      } else {
        setAutoplayMode(AutoplayMode.Unset);
        if (player) {
          const state = await player.getPlayerState();
          if (state === YouTube.PlayerState.PLAYING || latestIsAdPlayingReference.current) {
            setManualPaused(false);
          }
        }
      }
    };
    void run();
  }, [
    adsManager,
    enableAutoPlay,
    intersection,
    latestIsAdPlayingReference,
    isImaInitialized,
    latestManualPausedReference,
    player,
    setAutoplayMode,
    setManualPaused,
    isDesktopUp,
    latestIsAdStarted,
    uniqueVideoId,
  ]);
  useOnlyOnePlayerEffect(uniqueVideoId, player, setAutoplayMode);

  return (
    <Container>
      <VideoContainer ref={captureIntersectionElement}>
        <StyledYouTube
          id={uniqueVideoId}
          opts={playerOptions}
          title={data?.content?.headline ?? ""}
          videoId={videoId}
          onEnd={handleOnEnd}
          onPause={() => {
            // Prevent when ad started trigger the pause event
            !isAdStarted && handleOnPause();
          }}
          onReady={(event: { target: YouTubePlayer }) => {
            void event.target.mute();
            initImaManager(event.target, uniqueVideoId, adElementId);
            handleOnReady(event);
          }}
          onStateChange={handleOnStateChange}
        />
        <AdContainer id={adElementId} />
      </VideoContainer>
    </Container>
  );
};

SCMPYouTubeVideoPlayer.displayName = "SCMPYouTubeVideoPlayer";
